import { type FC, useCallback } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import { classNames } from '@/utils/helpers'

import { Checkbox } from '../checkbox.tsx'

interface Props {
  label?: string | React.ReactNode
  name: string
  className?: string
  disabled?: boolean
  checked?: boolean
  onChange?: (checked: CheckedState) => void
  defaultChecked?: CheckedState
  testId?: string
}

export type CheckedState = boolean | 'indeterminate'

export const FormCheckbox: FC<Props> = ({
  label,
  name,
  className,
  disabled,
  checked,
  onChange,
  defaultChecked = false,
  testId,
}) => {
  const { control } = useFormContext()
  const { field } = useController({
    name,
    control,
    defaultValue: defaultChecked,
  })

  const handleChange = useCallback(
    (checked: CheckedState) => {
      field.onChange(checked)

      if (onChange) {
        onChange(checked)
      }
    },
    [field, onChange],
  )

  return (
    <label
      htmlFor={field.name}
      className={classNames('flex items-center gap-2', className)}
    >
      <Checkbox
        checked={checked ?? field.value}
        id={field.name}
        onCheckedChange={handleChange}
        {...field}
        disabled={disabled}
        ref={field.ref}
        className={classNames('border', {
          'opacity-50 hover:cursor-not-allowed': disabled,
        })}
        data-testid={testId}
      />
      {label && (
        <span
          className={classNames('cursor-pointer text-sm text-title', {
            'cursor-not-allowed opacity-50': disabled,
          })}
        >
          {label}
        </span>
      )}
    </label>
  )
}
